import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ViewHeader, formatDate } from "@/components/admin/AdminShared";

const empty = { name: "", email: "", password: "", confirm: "" };

export const AdminLoginsView = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/admin/admins", { name: form.name, email: form.email, password: form.password });
      toast.success(`Admin login created for ${form.email}`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete the admin login for ${a.email}?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Admin login deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div>
      <Label htmlFor={`adm-${key}`}>{label}</Label>
      <Input id={`adm-${key}`} data-testid={`admin-form-${key}`} type={type} required value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 bg-white" {...extra} />
    </div>
  );

  return (
    <div data-testid="admin-logins-view">
      <ViewHeader title="Create New Admin Login" subtitle="Admins can manage products, orders, clients and email accounts." />
      <form onSubmit={submit} className="bg-white rounded-2xl border border-purple-100 p-6 grid sm:grid-cols-2 gap-4 mb-10">
        {field("name", "Full name")}
        {field("email", "Email (used to log in)", "email")}
        {field("password", "Password (min 8 characters)", "password", { minLength: 8 })}
        {field("confirm", "Confirm password", "password", { minLength: 8 })}
        <div className="sm:col-span-2">
          <button type="submit" data-testid="admin-form-submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full brand-gradient text-white font-semibold disabled:opacity-50">
            <UserPlus className="w-4 h-4" /> {saving ? "Creating…" : "Create admin login"}
          </button>
        </div>
      </form>

      <h3 className="font-semibold text-lg text-purple-950 mb-3">Admin logins ({admins.length})</h3>
      <div data-testid="admin-logins-list" className="bg-white rounded-2xl border border-purple-100 divide-y divide-purple-50">
        {admins.map((a) => (
          <div key={a.id} data-testid={`admin-login-${a.email}`} className="flex items-center gap-3 px-5 py-4">
            <ShieldCheck className="w-5 h-5 text-purple-600" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-purple-950">{a.name} {a.id === user?.id && <span className="text-xs text-fuchsia-600">(you)</span>}</p>
              <p className="text-xs text-muted-foreground">{a.email} · added {formatDate(a.created_at)}</p>
            </div>
            {a.id !== user?.id && (
              <button data-testid={`admin-login-delete-${a.email}`} onClick={() => remove(a)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
